import axios from 'axios';
import { ApiResponse, FilteredBusesResponse, MonitoringConfig, StatusResponse } from '../types';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
console.log(API_BASE_URL);
console.log(API_BASE_URL);

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
    'ngrok-skip-browser-warning': 'true'
  },
});

// Add request interceptor to include auth token
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('auth_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export const busMonitoringAPI = {
  // Start monitoring
  startSearch: async (config: MonitoringConfig): Promise<ApiResponse<MonitoringConfig>> => {
    const response = await api.post('/start-search', config);
    return response.data;
  },

  // Get status
  getStatus: async (): Promise<StatusResponse> => {
    const response = await api.get('/status');
    return response.data;
  },

  // Stop monitoring
  stopSearch: async (): Promise<ApiResponse> => {
    const response = await api.post('/stop-search');
    return response.data;
  },

  // Get filtered buses
  getFilteredBuses: async (): Promise<FilteredBusesResponse> => {
    const response = await api.get('/get-filtered-buses');
    return response.data;
  },
};

export const authAPI = {
  // Google OAuth authentication
  googleAuth: async (token: string): Promise<any> => {
    const response = await api.post('/auth/google', { token });
    return response.data;
  },

  // Verify authentication
  verifyAuth: async (): Promise<any> => {
    const response = await api.get('/auth/verify');
    return response.data;
  },

  // Logout
  logout: async (): Promise<any> => {
    const response = await api.post('/auth/logout');
    return response.data;
  },
};

export const accessRequestAPI = {
  // Create access request (public)
  createRequest: async (email: string, name: string, message: string): Promise<any> => {
    const response = await api.post('/access-request', { email, name, message });
    return response.data;
  },

  // Get request status (public)
  getRequestStatus: async (email: string): Promise<any> => {
    const response = await api.get(`/access-request/status/${encodeURIComponent(email)}`);
    return response.data;
  },
};

export const adminAPI = {
  // Get all users (admin only)
  getUsers: async (): Promise<any> => {
    const response = await api.get('/admin/users');
    return response.data;
  },

  // Add a new user (admin only)
  addUser: async (email: string): Promise<any> => {
    const response = await api.post('/admin/users', { email });
    return response.data;
  },

  // Remove a user (admin only)
  removeUser: async (email: string): Promise<any> => {
    const response = await api.delete(`/admin/users/${encodeURIComponent(email)}`);
    return response.data;
  },

  // Get user statistics (admin only)
  getStats: async (): Promise<any> => {
    const response = await api.get('/admin/stats');
    return response.data;
  },

  // Get access requests (admin only)
  getAccessRequests: async (status?: string): Promise<any> => {
    const url = status ? `/admin/access-requests?status=${status}` : '/admin/access-requests';
    const response = await api.get(url);
    return response.data;
  },

  // Process access request (admin only)
  processAccessRequest: async (requestId: string, action: 'approved' | 'rejected', adminNotes?: string): Promise<any> => {
    const response = await api.post(`/admin/access-requests/${requestId}/process`, {
      action,
      admin_notes: adminNotes || ''
    });
    return response.data;
  },

  // Get monitoring status for all users (admin only)
  getAllMonitoringStatus: async (): Promise<any> => {
    const response = await api.get('/admin/monitoring-status');
    return response.data;
  },
};

export const userAPI = {
  // Get user profile
  getProfile: async (): Promise<any> => {
    const response = await api.get('/user/profile');
    return response.data;
  },

  // Get user's Telegram user ID
  getTelegramId: async (): Promise<any> => {
    const response = await api.get('/user/telegram-id');
    return response.data;
  },

  // Update user's Telegram user ID
  updateTelegramId: async (telegramUserId: string): Promise<any> => {
    const response = await api.put('/user/telegram-id', {
      telegram_user_id: telegramUserId
    });
    return response.data;
  },
}; 